import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import TextField from '@mui/material/TextField';
import { DropzoneAreaBase } from 'material-ui-dropzone';
import { NON_ERASABLE_SUBSECTION_ID, ENDPOINT, LIMIT_PAGE } from 'constants/variables';
import { successErrorAction, warningErrorAction } from 'actions/ErrorActions';
import { helpHttp } from 'service/HelpHttp';
import { setLoading } from 'reducers/loadingSlice';
import TableSortingSubSection from 'pages/commonsarticle/departmentSettings/SortingTablesSubSection';
import 'css/generalSettings.css';
import 'css/customDialog.css';

const INITIAL_FILTERS = {
  idFrom: '',
  idTo: '',
  codeFrom: '',
  codeTo: '',
  nameesFrom: '',
  nameesTo: '',
  nameenFrom: '',
  nameenTo: '',
};
const EMPTY_SUBSECTION = {
  id: '',
  code: '',
  namees: '',
  nameen: '',
  filename: '',
  createdate: null,
  datafile: '',
  type: '',
};
const INITIAL_DATA = [EMPTY_SUBSECTION];
const HEAD_CELLS = [
  { id: 'id', numeric: false, disablePadding: false, label: 'Text_Code', canOrder: true },
  { id: 'code', numeric: false, disablePadding: false, label: 'Text_CodeSubSection', canOrder: true },
  { id: 'namees', numeric: false, disablePadding: false, label: 'Text_NameEs', canOrder: true },
  { id: 'nameen', numeric: false, disablePadding: false, label: 'Text_NameEn', canOrder: true },
  { id: 'image', numeric: false, disablePadding: false, label: 'TextImage', canOrder: false },
];
const INVALID_CHARACTERS_REGEX = /[!$%&|<>?{}~"']+/;
const NUMBER_REGEX = /^\d+$/;
const hasValue = (value) => value !== undefined && value !== null && value !== '';
const normalizeString = (value) => String(value || '').trim().toLowerCase();
const buildOffsetRanges = (total) => {
  if (total <= LIMIT_PAGE) {
    return [{ value: 0, label: `1 - ${Number(total || LIMIT_PAGE).toLocaleString('de-DE')}` }];
  }
  const ranges = [];
  for (let offset = 0; offset < total; offset += Number(LIMIT_PAGE)) {
    const from = offset + 1;
    const to = Math.min(offset + Number(LIMIT_PAGE), total);
    ranges.push({
      value: offset,
      label: `${from.toLocaleString('de-DE')} - ${to.toLocaleString('de-DE')}`,
    });
  }
  return ranges;
};

const subSectionToFileObject = async (subSection) => {
  if (!subSection?.filename || !subSection?.datafile || !subSection?.type) {
    return [];
  }
  const dataUrl = `data:${subSection.type};base64,${subSection.datafile}`;
  const blob = await (await fetch(dataUrl)).blob();
  const file = new File([blob], subSection.filename, {
    type: subSection.type,
    lastModified: subSection.createdate ? new Date(subSection.createdate).getTime() : Date.now(),
  });

  return [{ data: dataUrl, file }];
};

const SubSectionSettings = ({ openSubSection, setOpenSubSection, rowEditSection }) => {
  const { t, i18n } = useTranslation(['listDataArticles']);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginUser.token);
  const roles = useSelector((state) => state.loginUser.roles);
  const deleteCode = useSelector((state) => state.formatDIUser.delete_code);
  const isLoadingData = useSelector((state) => state.loading.isLoading);
  const [dataSubSection, setDataSubSection] = useState(INITIAL_DATA);
  const [formSubSection, setFormSubSection] = useState(EMPTY_SUBSECTION);
  const [rowEditSubSection, setRowEditSubSection] = useState(EMPTY_SUBSECTION);
  const [filtersSubSection, setFiltersSubSection] = useState(INITIAL_FILTERS);
  const [applyFiltersSubSection, setApplyFiltersSubSection] = useState(INITIAL_FILTERS);
  const [numTotRegSubSection, setNumTotRegSubSection] = useState(0);
  const [numTotRegFilter, setNumTotRegFilter] = useState(0);
  const [numTotRegSubSectionBySection, setNumTotRegSubSectionBySection] = useState(0);
  const [errorsFiltersSubSection, setErrorsFiltersSubSection] = useState({});
  const [errorsSubSection, setErrorsSubSection] = useState({});
  const [inputTextSubSection, setInputTextSubSection] = useState('');
  const [files, setFiles] = useState([]);
  const [openCreateDataSubSection, setOpenCreateDataSubSection] = useState(false);
  const [openDeleteRowSubSection, setOpenDeleteRowSubSection] = useState(false);
  const [openEditRowSubSection, setOpenEditRowSubSection] = useState(false);
  const [openEditFilterSubSection, setOpenEditFilterSubSection] = useState(false);
  const [isLoadingErrorSubSection, setIsLoadingErrorSubSection] = useState(false);
  const [idCodeDelete, setIdCodeDelete] = useState(deleteCode);
  const [activeOffset, setActiveOffset] = useState(false);
  const [currencyOffset, setCurrencyOffset] = useState(0);
  const [rangeOffset, setRangeOffset] = useState([{}]);
  const [refreshData, setRefreshData] = useState(false);

  const permissions = useMemo(() => {
    const menus = roles?.[0]?.listmenus || [];
    const hasPermission = (name) => menus.some((menu) => menu.name === name);

    return {
      canCreate: hasPermission('SubMenu_SubSection_Create'),
      canEdit: hasPermission('SubMenu_SubSection_Edit'),
      canDelete: hasPermission('SubMenu_SubSection_Delete'),
    };
  }, [roles]);

  const resetLocalState = useCallback(() => {
    setDataSubSection(INITIAL_DATA);
    setFormSubSection(EMPTY_SUBSECTION);
    setFiltersSubSection(INITIAL_FILTERS);
    setApplyFiltersSubSection(INITIAL_FILTERS);
    setNumTotRegSubSection(0);
    setNumTotRegFilter(0);
    setNumTotRegSubSectionBySection(0);
    setErrorsFiltersSubSection({});
    setErrorsSubSection({});
    setInputTextSubSection('');
    setOpenCreateDataSubSection(false);
    setOpenDeleteRowSubSection(false);
    setOpenEditRowSubSection(false);
    setOpenEditFilterSubSection(false);
    setRowEditSubSection(EMPTY_SUBSECTION);
    setIsLoadingErrorSubSection(false);
    setIdCodeDelete(deleteCode);
    setActiveOffset(false);
    setCurrencyOffset(0);
    setRangeOffset([{}]);
    setRefreshData(false);
    setFiles([]);
    dispatch(setLoading(false));
  }, [deleteCode, dispatch]);

  const closeModalSubSectionSettings = useCallback(() => {
    resetLocalState();
    setOpenSubSection(false);
  }, [resetLocalState, setOpenSubSection]);

  const validateFilterForm = useCallback(() => {
    const errors = {};

    if (hasValue(filtersSubSection.idFrom) && !NUMBER_REGEX.test(filtersSubSection.idFrom)) {
      errors.idFrom = 'Text_TextNoNumber';
    }
    if (hasValue(filtersSubSection.idTo) && !NUMBER_REGEX.test(filtersSubSection.idTo)) {
      errors.idTo = 'Text_TextNoNumber';
    }
    if (
      hasValue(filtersSubSection.idFrom) &&
      hasValue(filtersSubSection.idTo) &&
      !errors.idFrom &&
      !errors.idTo &&
      Number(filtersSubSection.idTo) < Number(filtersSubSection.idFrom)
    ) {
      errors.idTo = 'Text_TextGreater_Id';
    }

    ['codeFrom', 'codeTo', 'nameesFrom', 'nameesTo', 'nameenFrom', 'nameenTo'].forEach((field) => {
      if (INVALID_CHARACTERS_REGEX.test(filtersSubSection[field])) {
        errors[field] = 'Text_TextNoValid';
      }
    });

    if (hasValue(filtersSubSection.codeTo) && filtersSubSection.codeTo < filtersSubSection.codeFrom) {
      errors.codeTo = 'Text_TextGreater_SubSectiontCode';
    }
    if (hasValue(filtersSubSection.nameesTo) && filtersSubSection.nameesTo < filtersSubSection.nameesFrom) {
      errors.nameesTo = 'Text_TextGreater_Namees';
    }
    if (hasValue(filtersSubSection.nameenTo) && filtersSubSection.nameenTo < filtersSubSection.nameenFrom) {
      errors.nameenTo = 'Text_TextGreater_Nameen';
    }

    return errors;
  }, [filtersSubSection]);

  const validateSubSectionForm = useCallback(() => {
    const errors = {};
    const currentId = rowEditSubSection.id;

    const codeExists = dataSubSection.some(
      (item) => normalizeString(item.code) === normalizeString(formSubSection.code) && (!openEditRowSubSection || item.id !== currentId),
    );
    const nameEsExists = dataSubSection.some(
      (item) => normalizeString(item.namees) === normalizeString(formSubSection.namees) && (!openEditRowSubSection || item.id !== currentId),
    );
    const nameEnExists = dataSubSection.some(
      (item) => normalizeString(item.nameen) === normalizeString(formSubSection.nameen) && (!openEditRowSubSection || item.id !== currentId),
    );

    if (!hasValue(formSubSection.code)) {
      errors.code = 'Text_FieldCodeSubSectionRequired';
    } else if (codeExists) {
      errors.code = 'Text_SubSection_CodeExist';
    }

    if (!hasValue(formSubSection.namees)) {
      errors.namees = 'Text_FieldNameesRequired';
    } else if (INVALID_CHARACTERS_REGEX.test(formSubSection.namees)) {
      errors.namees = 'Text_TextNoValid';
    } else if (nameEsExists) {
      errors.namees = 'Text_SubSection_NameesExist';
    }

    if (!hasValue(formSubSection.nameen)) {
      errors.nameen = 'Text_FieldNameenRequired';
    } else if (INVALID_CHARACTERS_REGEX.test(formSubSection.nameen)) {
      errors.nameen = 'Text_TextNoValid';
    } else if (nameEnExists) {
      errors.nameen = 'Text_SunSection_NameenExist';
    }

    return errors;
  }, [dataSubSection, formSubSection, openEditRowSubSection, rowEditSubSection.id]);

  const filteredDataSubSection = useMemo(() => {
    const search = normalizeString(inputTextSubSection);
    if (!search) {
      return dataSubSection;
    }

    return dataSubSection.filter((item) => {
      const searchable = [item.id, item.code, item.namees, item.nameen].map((value) => String(value || '')).join(' ').toLowerCase();
      return searchable.includes(search);
    });
  }, [dataSubSection, inputTextSubSection]);

  const openCreateOrEditDataSubSection = useCallback(() => {
    setInputTextSubSection('');
    setErrorsSubSection({});
    setFormSubSection(EMPTY_SUBSECTION);
    setFiles([]);
    setOpenEditRowSubSection(false);
    setOpenCreateDataSubSection(true);
  }, []);

  const openEditFiltersSubSection = useCallback(() => {
    setFiltersSubSection(applyFiltersSubSection);
    setErrorsFiltersSubSection({});
    setOpenEditFilterSubSection((prev) => !prev);
  }, [applyFiltersSubSection]);

  const handleChangeFilterEditSubSection = useCallback((event) => {
    const { name, value } = event.target;
    setFiltersSubSection((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurFilterEditSubSection = useCallback(
    (event) => {
      const { name, value } = event.target;
      const nextFilters = { ...filtersSubSection, [name]: value };
      setFiltersSubSection(nextFilters);

      const errors = {};
      if (hasValue(nextFilters.idFrom) && !NUMBER_REGEX.test(nextFilters.idFrom)) {
        errors.idFrom = 'Text_TextNoNumber';
      }
      if (hasValue(nextFilters.idTo) && !NUMBER_REGEX.test(nextFilters.idTo)) {
        errors.idTo = 'Text_TextNoNumber';
      }
      if (
        hasValue(nextFilters.idFrom) &&
        hasValue(nextFilters.idTo) &&
        !errors.idFrom &&
        !errors.idTo &&
        Number(nextFilters.idTo) < Number(nextFilters.idFrom)
      ) {
        errors.idTo = 'Text_TextGreater_Id';
      }
      ['codeFrom', 'codeTo', 'nameesFrom', 'nameesTo', 'nameenFrom', 'nameenTo'].forEach((field) => {
        if (INVALID_CHARACTERS_REGEX.test(nextFilters[field])) {
          errors[field] = 'Text_TextNoValid';
        }
      });
      if (hasValue(nextFilters.codeTo) && nextFilters.codeTo < nextFilters.codeFrom) {
        errors.codeTo = 'Text_TextGreater_SubSectiontCode';
      }
      if (hasValue(nextFilters.nameesTo) && nextFilters.nameesTo < nextFilters.nameesFrom) {
        errors.nameesTo = 'Text_TextGreater_Namees';
      }
      if (hasValue(nextFilters.nameenTo) && nextFilters.nameenTo < nextFilters.nameenFrom) {
        errors.nameenTo = 'Text_TextGreater_Nameen';
      }

      setErrorsFiltersSubSection(errors);
    },
    [filtersSubSection],
  );

  const handleSubmitFilterEditSubSection = useCallback(() => {
    const errors = validateFilterForm();
    setErrorsFiltersSubSection(errors);

    if (Object.keys(errors).length > 0) {
      dispatch(warningErrorAction(t('Text_Error_Filter')));
      return;
    }

    setApplyFiltersSubSection(filtersSubSection);
    setCurrencyOffset(0);
    setErrorsFiltersSubSection({});
    setOpenEditFilterSubSection(false);
  }, [dispatch, filtersSubSection, t, validateFilterForm]);

  const handleDeleteFiltersSubSection = useCallback(() => {
    setErrorsFiltersSubSection({});
    setFiltersSubSection(INITIAL_FILTERS);
    setApplyFiltersSubSection(INITIAL_FILTERS);
    setCurrencyOffset(0);
    setOpenEditFilterSubSection(false);
  }, []);

  const toggleDrawerFilterEditSubSection = useCallback(
    (open) => (event) => {
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }
      if (!open) {
        setErrorsFiltersSubSection({});
        setFiltersSubSection(INITIAL_FILTERS);
        setOpenEditFilterSubSection(false);
      }
    },
    [],
  );

  const toggleDrawerSubSection = useCallback(
    (open) => (event) => {
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }
      if (!open) {
        closeModalSubSectionSettings();
      }
    },
    [closeModalSubSectionSettings],
  );

  const inputHandlerSubSection = useCallback((event) => {
    setInputTextSubSection(event.target.value.toLowerCase());
  }, []);

  const handleChangeDeleteRowSubSection = useCallback((event) => {
    setIdCodeDelete(event.target.value);
  }, []);

  const handleCancelDeleteRowSubSection = useCallback(() => {
    setErrorsSubSection({});
    setRowEditSubSection(EMPTY_SUBSECTION);
    setFormSubSection(EMPTY_SUBSECTION);
    setIdCodeDelete(deleteCode);
    setIsLoadingErrorSubSection(false);
    setOpenDeleteRowSubSection(false);
    dispatch(setLoading(false));
  }, [deleteCode, dispatch]);

  const toBase64 = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      }),
    [],
  );

  const handleAddFileImage = useCallback((newFiles) => {
    setFiles([...newFiles]);
  }, []);

  const handleDeleteFileImage = useCallback((deletedFile) => {
    setFiles((prev) => prev.filter((file) => file !== deletedFile));
  }, []);

  const buildUpdatedFiles = useCallback(async () => {
    return Promise.all(
      files.map(async (item, index) => {
        const file = item.file || item;
        const base64 = await toBase64(file);
        return {
          id: index,
          filename: file.name,
          createdate: file.lastModified,
          datafile: base64,
          type: file.type,
        };
      }),
    );
  }, [files, toBase64]);

  const handleCloseDataEditSubSection = useCallback(() => {
    setOpenCreateDataSubSection(false);
    setOpenEditRowSubSection(false);
    setErrorsSubSection({});
    setRefreshData(false);
    setFormSubSection(EMPTY_SUBSECTION);
    setFiles([]);
    setIsLoadingErrorSubSection(false);
    dispatch(setLoading(false));
  }, [dispatch]);

  const handleChangeDataEditSubSection = useCallback((event) => {
    const { name, value } = event.target;
    setFormSubSection((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurDataEditSubSection = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormSubSection((prev) => {
        const updated = { ...prev, [name]: value };
        const currentId = rowEditSubSection.id;
        const errors = {};

        const codeExists = dataSubSection.some(
          (item) => normalizeString(item.code) === normalizeString(updated.code) && (!openEditRowSubSection || item.id !== currentId),
        );
        const nameEsExists = dataSubSection.some(
          (item) => normalizeString(item.namees) === normalizeString(updated.namees) && (!openEditRowSubSection || item.id !== currentId),
        );
        const nameEnExists = dataSubSection.some(
          (item) => normalizeString(item.nameen) === normalizeString(updated.nameen) && (!openEditRowSubSection || item.id !== currentId),
        );

        if (!hasValue(updated.code)) {
          errors.code = 'Text_FieldCodeSubSectionRequired';
        } else if (codeExists) {
          errors.code = 'Text_SubSection_CodeExist';
        }

        if (!hasValue(updated.namees)) {
          errors.namees = 'Text_FieldNameesRequired';
        } else if (INVALID_CHARACTERS_REGEX.test(updated.namees)) {
          errors.namees = 'Text_TextNoValid';
        } else if (nameEsExists) {
          errors.namees = 'Text_SubSection_NameesExist';
        }

        if (!hasValue(updated.nameen)) {
          errors.nameen = 'Text_FieldNameenRequired';
        } else if (INVALID_CHARACTERS_REGEX.test(updated.nameen)) {
          errors.nameen = 'Text_TextNoValid';
        } else if (nameEnExists) {
          errors.nameen = 'Text_SunSection_NameenExist';
        }

        setErrorsSubSection(errors);
        return updated;
      });
    },
    [dataSubSection, openEditRowSubSection, rowEditSubSection.id],
  );

  const toggleDrawerDataEditSubSection = useCallback(
    (open) => (event) => {
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }
      if (!open) {
        handleCloseDataEditSubSection();
      }
    },
    [handleCloseDataEditSubSection],
  );

  const submitSubSectionRequest = useCallback(
    async (payload) => {
      dispatch(setLoading(true));
      try {
        const response = openEditRowSubSection
          ? await helpHttp().put(ENDPOINT, 'subsection/updatesubsection', token, payload)
          : await helpHttp().post2(ENDPOINT, 'subsection/savesubsection', token, rowEditSection.id, payload);

        if (response.status === 'OK') {
          dispatch(successErrorAction(t('Text_SaveData')));
          setRefreshData(true);
          handleCloseDataEditSubSection();
          return;
        }

        dispatch(warningErrorAction(t(response.cause)));
      } catch (error) {
        dispatch(warningErrorAction(t('Text_IncorrectData')));
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, handleCloseDataEditSubSection, openEditRowSubSection, rowEditSection.id, t, token],
  );

  const handleSubmitDataEditSubSection = useCallback(async () => {
    const errors = validateSubSectionForm();
    setErrorsSubSection(errors);

    if (Object.keys(errors).length > 0) {
      if (errors.code) dispatch(warningErrorAction(t(errors.code)));
      else if (errors.namees) dispatch(warningErrorAction(t(errors.namees)));
      else if (errors.nameen) dispatch(warningErrorAction(t(errors.nameen)));
      else dispatch(warningErrorAction(t('Text_IncorrectData')));
      return;
    }

    const updatedFiles = files.length > 0 ? await buildUpdatedFiles() : [];
    const firstFile = updatedFiles[0];

    const payload = {
      ...formSubSection,
      filename: firstFile?.filename || '',
      createdate: firstFile?.createdate || null,
      datafile: firstFile?.datafile || '',
      type: firstFile?.type || '',
    };

    setFormSubSection(payload);
    await submitSubSectionRequest(payload);
  }, [buildUpdatedFiles, dispatch, files.length, formSubSection, submitSubSectionRequest, t, validateSubSectionForm]);

  const handleCloseDeleteRowSubSection = useCallback(async () => {
    if (idCodeDelete !== deleteCode) {
      dispatch(warningErrorAction(t('Text_InvalidCode')));
      return;
    }

    if (!rowEditSubSection.id) {
      dispatch(warningErrorAction(t('Text_InvalidId')));
      return;
    }

    dispatch(setLoading(true));
    try {
      const response = await helpHttp().del(ENDPOINT, 'subsection/deletesubsection', token, rowEditSubSection);
      if (response.status === 'OK') {
        dispatch(successErrorAction(t('Text_ValidCode')));
        dispatch(successErrorAction(t('Text_DeleteField')));
        setRefreshData(true);
      } else {
        dispatch(warningErrorAction(t(response.cause)));
      }
    } catch (error) {
      dispatch(warningErrorAction(t('Text_IncorrectData')));
    } finally {
      dispatch(setLoading(false));
      setIdCodeDelete(deleteCode);
      setOpenDeleteRowSubSection(false);
      handleCloseDataEditSubSection();
    }
  }, [deleteCode, dispatch, handleCloseDataEditSubSection, idCodeDelete, rowEditSubSection, t, token]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && openSubSection) {
        closeModalSubSectionSettings();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [closeModalSubSectionSettings, openSubSection]);

  useEffect(() => {
    let cancelled = false;

    const syncEditForm = async () => {
      if (!openEditRowSubSection) {
        setFormSubSection(EMPTY_SUBSECTION);
        setFiles([]);
        return;
      }

      setFormSubSection({
        id: rowEditSubSection.id ?? '',
        code: rowEditSubSection.code ?? '',
        namees: rowEditSubSection.namees ?? '',
        nameen: rowEditSubSection.nameen ?? '',
        filename: rowEditSubSection.filename ?? '',
        createdate: rowEditSubSection.createdate ?? null,
        datafile: rowEditSubSection.datafile ?? '',
        type: rowEditSubSection.type ?? '',
      });

      const mappedFiles = await subSectionToFileObject(rowEditSubSection);
      if (!cancelled) {
        setFiles(mappedFiles);
      }
    };

    syncEditForm();

    return () => {
      cancelled = true;
    };
  }, [openEditRowSubSection, rowEditSubSection]);

  useEffect(() => {
    setActiveOffset(numTotRegFilter > LIMIT_PAGE);
    setRangeOffset(numTotRegFilter > LIMIT_PAGE ? buildOffsetRanges(numTotRegFilter) : [{}]);
    if (numTotRegFilter <= LIMIT_PAGE) {
      setCurrencyOffset(0);
    }
  }, [numTotRegFilter]);

  useEffect(() => {
    let ignore = false;

    const fetchAll = async () => {
      if ((!openSubSection && !refreshData) || !rowEditSection?.id) {
        return;
      }

      dispatch(setLoading(true));
      try {
        const [totalResponse, filteredTotalResponse, totalBySectionResponse, dataResponse] = await Promise.all([
          helpHttp().get(ENDPOINT, 'subsection/getinformationtablesubsection', token),
          helpHttp().post2(ENDPOINT, 'subsection/getcountfiltersubsection', token, rowEditSection.id, applyFiltersSubSection),
          helpHttp().get2(ENDPOINT, 'subsection/getinformationtablesubsectionbysection/', token, rowEditSection.id),
          helpHttp().get4(ENDPOINT, 'subsection/searchsubsectionssections', token, rowEditSection.id, currencyOffset, LIMIT_PAGE, applyFiltersSubSection),
        ]);

        if (ignore) {
          return;
        }

        const hasConnectionError = (response) => response?.err || response?.message === 'Load failed';
        const hasAuthError = (response) => response?.err;

        if (
          hasConnectionError(totalResponse) ||
          hasConnectionError(filteredTotalResponse) ||
          hasConnectionError(totalBySectionResponse) ||
          hasConnectionError(dataResponse)
        ) {
          setIsLoadingErrorSubSection(true);
          setDataSubSection(INITIAL_DATA);
          dispatch(
            warningErrorAction(
              t(dataResponse?.message === 'Load failed' ? 'Text_ConnectionFailied' : hasAuthError(dataResponse) ? 'Text_InvalidCredentials' : 'Text_IncorrectData'),
            ),
          );
          return;
        }

        setNumTotRegSubSection(totalResponse);
        setNumTotRegFilter(filteredTotalResponse);
        setNumTotRegSubSectionBySection(totalBySectionResponse);
        setDataSubSection(dataResponse);
        setIsLoadingErrorSubSection(false);
      } catch (error) {
        if (!ignore) {
          setIsLoadingErrorSubSection(true);
          setDataSubSection(INITIAL_DATA);
          dispatch(warningErrorAction(t('Text_IncorrectData')));
        }
      } finally {
        if (!ignore) {
          dispatch(setLoading(false));
          setRefreshData(false);
        }
      }
    };

    fetchAll();

    return () => {
      ignore = true;
    };
  }, [applyFiltersSubSection, currencyOffset, dispatch, openSubSection, refreshData, rowEditSection?.id, t, token]);

  if (!openSubSection) {
    return null;
  }

  return (
    <div>
      <SwipeableDrawer anchor="right" open onClose={toggleDrawerSubSection(false)} onOpen={toggleDrawerSubSection(true)}>
        <Container>
          <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '1050px' }}>
            <br />
            <div className="formdi-pending-header2">
              <h1 className="formdi-pending-title">
                {isLoadingErrorSubSection ? (
                  <i className="fas fa-solid fa-exclamation fa-2x text-danger" />
                ) : isLoadingData ? (
                  <CircularProgress color={openDeleteRowSubSection ? 'warning' : 'primary'} />
                ) : (
                  <i className="fas fa-solid fa-store text-warning" />
                )}{' '}
                {t('Text_Group_Config')}
              </h1>

              <div>
                {permissions.canCreate && (
                  <button
                    type="button"
                    className="formdi-pending-btn formdi-pending-btn--primary"
                    onClick={openCreateOrEditDataSubSection}
                  >
                    {t('Button_Create')}
                  </button>
                )}
                {permissions.canCreate && <span>&nbsp;&nbsp;</span>}
                <button
                  type="button"
                  className="formdi-pending-btn formdi-pending-btn--secondary"
                  onClick={closeModalSubSectionSettings}
                >
                  {t('Button_Back')}
                </button>
              </div>
            </div>

            <div id="myDIV">
              <h1 className="h1NewStyle mb-2">
                {i18n.language === 'es' ? rowEditSection.namees || rowEditSection.name : rowEditSection.nameen || rowEditSection.name}
              </h1>
            </div>

            <div className="formdi-pending-search">
              <TextField
                id="outlined-basic"
                variant="outlined"
                value={inputTextSubSection}
                inputProps={{ style: { fontSize: '18px' } }}
                InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                placeholder={t('Text_Group_Search')}
                onChange={inputHandlerSubSection}
                fullWidth
                label={t('Text_Group_Search')}
              />
            </div>

            <br />

            <div id="myNewDIV" className="formdi-pending-filters">
              <h1 className="formdi-pending-filter-title">{t('Text_Filter')}</h1>
              <div className="formdi-pending-filters-row">
                <button
                  type="button"
                  className={`formdi-pending-chip ${applyFiltersSubSection.idFrom || applyFiltersSubSection.idTo ? 'formdi-pending-chip--active' : ''}`}
                  onClick={openEditFiltersSubSection}
                >
                  <span
                    className={`formdi-pending-dot ${applyFiltersSubSection.idFrom || applyFiltersSubSection.idTo ? 'formdi-pending-dot--on' : ''}`}
                  />
                  {t('Text_Code')}
                </button>

                <button
                  type="button"
                  className={`formdi-pending-chip ${applyFiltersSubSection.codeFrom || applyFiltersSubSection.codeTo ? 'formdi-pending-chip--active' : ''}`}
                  onClick={openEditFiltersSubSection}
                >
                  <span
                    className={`formdi-pending-dot ${applyFiltersSubSection.codeFrom || applyFiltersSubSection.codeTo ? 'formdi-pending-dot--on' : ''}`}
                  />
                  {t('Text_CodeSubSection')}
                </button>

                <button
                  type="button"
                  className={`formdi-pending-chip ${applyFiltersSubSection.nameesFrom || applyFiltersSubSection.nameesTo ? 'formdi-pending-chip--active' : ''}`}
                  onClick={openEditFiltersSubSection}
                >
                  <span
                    className={`formdi-pending-dot ${applyFiltersSubSection.nameesFrom || applyFiltersSubSection.nameesTo ? 'formdi-pending-dot--on' : ''}`}
                  />
                  {t('Text_NameEs')}
                </button>

                <button
                  type="button"
                  className={`formdi-pending-chip ${applyFiltersSubSection.nameenFrom || applyFiltersSubSection.nameenTo ? 'formdi-pending-chip--active' : ''}`}
                  onClick={openEditFiltersSubSection}
                >
                  <span
                    className={`formdi-pending-dot ${applyFiltersSubSection.nameenFrom || applyFiltersSubSection.nameenTo ? 'formdi-pending-dot--on' : ''}`}
                  />
                  {t('Text_NameEn')}
                </button>
              </div>
            </div>

            <br />
            <hr className="sidebar-divider d-none d-md-block" />

            <div className="formdi-pending-card-flat">
              <div className="formdi-pending-card-content">
                <TableSortingSubSection
                  dataSubSection={filteredDataSubSection}
                  headCellsSubSection={HEAD_CELLS}
                  numTotRegFilter={numTotRegFilter}
                  titleTable={t('Text_Group_Table')}
                  setOpenCreateDataSubSection={setOpenCreateDataSubSection}
                  setOpenEditRowSubSection={setOpenEditRowSubSection}
                  setOpenDeleteRowSubSection={setOpenDeleteRowSubSection}
                  setRowEditSubSection={setRowEditSubSection}
                  showEditButtonSubSection={permissions.canEdit}
                  showDeleteButtonSubSection={permissions.canDelete}
                  numTotRegSubSection={numTotRegSubSection}
                  numTotRegSubSectionBySection={numTotRegSubSectionBySection}
                  activeOffset={activeOffset}
                  rangeOffset={rangeOffset}
                  currencyOffset={currencyOffset}
                  setCurrencyOffset={setCurrencyOffset}
                  minvalue={Number(NON_ERASABLE_SUBSECTION_ID)}
                />
              </div>
            </div>
          </Box>
        </Container>
      </SwipeableDrawer>

      {openEditFilterSubSection && (
        <SwipeableDrawer
          anchor="right"
          open
          onClose={toggleDrawerFilterEditSubSection(false)}
          onOpen={toggleDrawerFilterEditSubSection(true)}
        >
          <Container>
            <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '500px' }}>
              <br />
              <div id="myDIV">
                <h1 className="h1NewStyle px-4 mb-2">{t('Text_Filter')}</h1>
              </div>
              <hr className="sidebar-divider d-none d-md-block" />

              <form className="createfilter" onSubmit={(e) => e.preventDefault()}>
                <div className="form-floating mb-3 px-4 primary">
                  {[
                    ['idFrom', 'Text_Filter_Code_From', 'PlaceHolder_Filter_Code_From', 15],
                    ['idTo', 'Text_Filter_Code_To', 'PlaceHolder_Filter_Code_to', 15],
                    ['codeFrom', 'Text_Filter_Grupo_CodeFrom', 'PlaceHolder_SubSection_CodeFrom', 15],
                    ['codeTo', 'Text_Filter_NameEs_To', 'PlaceHolder_SubSection_CodeTo', 10],
                    ['nameesFrom', 'Text_Filter_NameEs_From', 'PlaceHolder_Filter_NameEs_From', 100],
                    ['nameesTo', 'Text_Filter_NameEs_To', 'PlaceHolder_Filter_NameEs_to', 100],
                    ['nameenFrom', 'Text_Filter_NameEn_From', 'PlaceHolder_Filter_NameEn_From', 100],
                    ['nameenTo', 'Text_Filter_NameEn_To', 'PlaceHolder_Filter_NameEn_to', 100],
                  ].map(([name, labelKey, placeholderKey, maxLength]) => (
                    <React.Fragment key={name}>
                      <label className="text-primary" style={{ fontSize: 18 }}>
                        {t(labelKey)} :
                      </label>
                      <div className="form-group">
                        <TextField
                          id={name}
                          name={name}
                          value={filtersSubSection[name]}
                          inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength }}
                          InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                          label={errorsFiltersSubSection[name] ? t(errorsFiltersSubSection[name]) : t(placeholderKey)}
                          placeholder={t(placeholderKey)}
                          variant="standard"
                          onBlur={handleBlurFilterEditSubSection}
                          onChange={handleChangeFilterEditSubSection}
                        />
                      </div>
                    </React.Fragment>
                  ))}

                  <hr className="sidebar-divider d-none d-md-block" />
                  <div className="formdi-pending-action-group">
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--primary"
                      type="button"
                      onClick={handleSubmitFilterEditSubSection}
                    >
                      {t('Button_Apply')}
                    </button>
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--secondary"
                      type="button"
                      onClick={handleDeleteFiltersSubSection}
                    >
                      {t('Button_Cancel_Filters')}
                    </button>
                  </div>
                </div>
              </form>
            </Box>
          </Container>
        </SwipeableDrawer>
      )}

      {(openCreateDataSubSection || openEditRowSubSection) && (
        <SwipeableDrawer
          anchor="right"
          open
          onClose={toggleDrawerDataEditSubSection(false)}
          onOpen={toggleDrawerDataEditSubSection(true)}
        >
          <Container>
            <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '500px' }}>
              <br />
              <div id="myDIV">
                <h1 className="h1NewStyle px-4 mb-2">
                  {openEditRowSubSection ? t('Text_Group_Edit') : t('Text_Group_Create')}
                </h1>
              </div>
              <hr className="sidebar-divider d-none d-md-block" />

              <form className="createsubsection" onSubmit={(e) => e.preventDefault()}>
                <div className="form-floating mb-3 px-4 primary">
                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_Code')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="id"
                      name="id"
                      value={formSubSection.id}
                      inputProps={{ style: { width: 200, fontSize: '18px' }, maxLength: 15 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={t('PlaceHolder_SubSection_Code')}
                      placeholder={t('PlaceHolder_SubSection_Code')}
                      variant="standard"
                      disabled
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_CodeSubSection')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="code"
                      name="code"
                      value={formSubSection.code}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 10 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsSubSection.code ? t(errorsSubSection.code) : t('Placeholder_CodeSubSection')}
                      placeholder={t('Placeholder_CodeSubSection')}
                      variant="standard"
                      onBlur={handleBlurDataEditSubSection}
                      onChange={handleChangeDataEditSubSection}
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_NameEs')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="namees"
                      name="namees"
                      value={formSubSection.namees}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 100 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsSubSection.namees ? t(errorsSubSection.namees) : t('PlaceHolder_SubSection_NameEs')}
                      placeholder={t('PlaceHolder_SubSection_NameEs')}
                      variant="standard"
                      onBlur={handleBlurDataEditSubSection}
                      onChange={handleChangeDataEditSubSection}
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_NameEn')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="nameen"
                      name="nameen"
                      value={formSubSection.nameen}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 100 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsSubSection.nameen ? t(errorsSubSection.nameen) : t('PlaceHolder_SunSection_NameEn')}
                      placeholder={t('PlaceHolder_SunSection_NameEn')}
                      variant="standard"
                      onBlur={handleBlurDataEditSubSection}
                      onChange={handleChangeDataEditSubSection}
                    />
                  </div>

                  <br />
                  <br />

                  <div className="upload-box" style={{ width: '100%' }}>
                    <DropzoneAreaBase
                      initialFiles={files}
                      fileObjects={files}
                      acceptedFiles={['image/*']}
                      maxFileSize={20000000}
                      filesLimit={1}
                      onAdd={handleAddFileImage}
                      onDelete={handleDeleteFileImage}
                      showAlerts={false}
                      dropzoneText={t('Text_Select_Image')}
                      showPreviews={false}
                      showPreviewsInDropzone={false}
                      showFileNamesInPreview={false}
                      showFileNames={false}
                      useChipsForPreview
                      previewGridProps={{ container: { spacing: 1, direction: 'row' } }}
                      previewChipProps={{ classes: { root: 'upload-preview-chip' } }}
                      classes={{
                        root: 'upload-dropzone-root',
                        icon: 'upload-dropzone-icon',
                        text: 'upload-dropzone-text',
                      }}
                    />

                    {files.length > 0 && (
                      <div className="preview-thumbnails-wrapper">
                        <div className="preview-thumbnails">
                          {files.map((fileObj, index) => (
                            <div key={index} className="thumbnail-container">
                              <img src={fileObj.data} alt={`preview-${index}`} className="thumbnail-image" />
                              <button
                                type="button"
                                onClick={() => handleDeleteFileImage(fileObj)}
                                className="thumbnail-delete-btn"
                                title={t('Text_Delete')}
                              >
                                &times;
                              </button>
                              &nbsp;&nbsp;
                              <Chip
                                value={fileObj.file.name}
                                label={fileObj.file.name}
                                clickable={false}
                                style={{ color: '#e74a3b', fontSize: 14 }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <hr className="sidebar-divider d-none d-md-block" />
                  <div className="formdi-pending-action-group">
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--primary"
                      type="button"
                      onClick={handleSubmitDataEditSubSection}
                    >
                      {openEditRowSubSection ? t('Button_Update') : t('Button_Create')}
                    </button>
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--secondary"
                      type="button"
                      onClick={handleCloseDataEditSubSection}
                    >
                      {t('Button_Cancel')}
                    </button>
                  </div>
                </div>
              </form>
            </Box>
          </Container>
        </SwipeableDrawer>
      )}

      <Dialog
        open={openDeleteRowSubSection}
        onClose={handleCancelDeleteRowSubSection}
        PaperProps={{ className: 'my-dialog-paper-danger' }}
      >
        <DialogTitle className="my-dialog-title-danger">
          {t('Text_Delete')} {rowEditSubSection.id} - {rowEditSubSection.code} -{' '}
          {i18n.language === 'es' ? rowEditSubSection.namees : rowEditSubSection.nameen}
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="my-dialog-contentText-danger">
            {t('Text_Line1')}
            <br />
            {t('Text_Line2')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="idCode"
            label={t('Text_Label')}
            type="text"
            fullWidth
            variant="standard"
            className="my-dialog-input-danger"
            value={idCodeDelete}
            onChange={handleChangeDeleteRowSubSection}
          />
        </DialogContent>
        <DialogActions className="my-dialog-actions">
          <Button onClick={handleCancelDeleteRowSubSection} className="my-dialog-button-cancel-danger">
            {t('Button_Cancel')}
          </Button>
          <Button variant="contained" onClick={handleCloseDeleteRowSubSection} className="my-dialog-button-delete">
            {t('Button_Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

SubSectionSettings.propTypes = {
  openSubSection: PropTypes.bool.isRequired,
  setOpenSubSection: PropTypes.func.isRequired,
  rowEditSection: PropTypes.object.isRequired,
};

export default SubSectionSettings;
